'use client';

import { useState, useRef, useMemo } from 'react';
import { message } from 'antd';
import { DealerRackGeneralInquiry } from '../types/types';

export default function InquiryForm() {
  const [formData, setFormData] = useState<DealerRackGeneralInquiry>({
    type: 'general',
    fullName: '',
    email: '',
    phone: '',
    company: '',
    interestedIn: '',
    message: '',
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const formRef = useRef<HTMLFormElement>(null);

  const isValid = useMemo(() => {
    if (formRef?.current) {
      return formRef.current.checkValidity();
    }
    return false;
  }, [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setIsProcessing(true);

    if (!isValid) {
      messageApi.error('Please provide your full name, a valid email address, and your message.');
      setIsProcessing(false);
      return;
    }

    
    try {
      const response = await fetch('/api/inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        messageApi.success('Your inquiry has been sent! A representative will contact you soon.', 5);
        setFormData({ 
          type: 'general',
          fullName: '', 
          email: '', 
          phone: '', 
          company: '', 
          interestedIn: '', 
          message: '' 
        });
      } else {
        messageApi.error('Failed to send inquiry. Please try again.', 5);
      }
    } catch (error) {
      messageApi.error('An error occurred. Please try again.', 5);
    }

    setIsProcessing(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="h-full">
      {contextHolder}
      <h3 className="text-2xl font-bold text-gray-900 mb-2">Make an Inquiry</h3>
      <p className="text-gray-600 text-sm mb-6">Tell us where you are and what you are looking for and we'll have a professional sales rep contact you today.</p>
      <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
            Full Name *
          </label>
          <input
            type="text"
            id="fullName"
            name="fullName"
            required
            value={formData.fullName}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-brand focus:border-brand transition-all duration-200 bg-gray-50 focus:bg-white"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            value={formData.email}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-brand focus:border-brand transition-all duration-200 bg-gray-50 focus:bg-white"
          />
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-brand focus:border-brand transition-all duration-200 bg-gray-50 focus:bg-white"
          />
        </div>

        <div>
          <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
            Company Name
          </label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-brand focus:border-brand transition-all duration-200 bg-gray-50 focus:bg-white"
          />
        </div>

        <div>
          <label htmlFor="interestedIn" className="block text-sm font-medium text-gray-700 mb-1">
            Interested in...
          </label>
          <input
            type="text"
            id="interestedIn"
            name="interestedIn"
            placeholder="e.g. forklifts, conveyors, racking, etc."
            value={formData.interestedIn}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-brand focus:border-brand transition-all duration-200 bg-gray-50 focus:bg-white"
          />
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
            Message
          </label>
          <textarea
            id="message"
            name="message"
            required
            rows={4}
            value={formData.message}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-brand focus:border-brand transition-all duration-200 resize-none bg-gray-50 focus:bg-white"
            placeholder="Tell us more about your equipment needs..."
          />
        </div>

        <button
          type="submit"
          disabled={isProcessing}
          className="w-full hover:cursor-pointer bg-brand hover:bg-brand-dark text-black font-bold py-3 rounded-lg shadow-md hover:shadow-lg transition-colors duration-200 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isProcessing ? 'Sending...' : 'Send'}
        </button>
      </form>
    </div>
  );
}
